import React, { useState, useEffect } from "react";
import { db } from "./firebase";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { collection, serverTimestamp, doc, setDoc, getDocs, deleteDoc, addDoc, getDoc } from "firebase/firestore";

export default function AdminEvents() {
    const [formData, setFormData] = useState({
        title: "",
        date: "",
        startTime: "",
        endTime: "",
        location: "",
        eventCost: "",
        topics: "Campus",
        eventType: "Offline",
        paymentLink: "",
        recapText: "",
        recapImg: "",
        recapMediaUrls: [],
        attendeesText: ""
    });
    const [image, setImage] = useState(null);
    const [qrCode, setQrCode] = useState(null);
    const [recapMediaFiles, setRecapMediaFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [events, setEvents] = useState([]);
    const [editingId, setEditingId] = useState(null);
    const [activeTab, setActiveTab] = useState("events"); // "events", "stats", "interest", "responses"

    // Stats State
    const [stats, setStats] = useState({
        totalEvents: "",
        totalAttendees: "",
        totalCities: ""
    });
    const [statsLoading, setStatsLoading] = useState(false);
    const [statsMessage, setStatsMessage] = useState("");
    const [statsLastUpdated, setStatsLastUpdated] = useState(null);
    const [statsHistory, setStatsHistory] = useState([]);
    const [expandedQuestionIndex, setExpandedQuestionIndex] = useState(0);
    const [isResponseMinimized, setIsResponseMinimized] = useState(false);

    // Interest Form State
    const [interestQuestions, setInterestQuestions] = useState([]);
    const [interestLoading, setInterestLoading] = useState(false);
    const [interestMessage, setInterestMessage] = useState("");
    const [interestLastUpdated, setInterestLastUpdated] = useState(null);
    const [interestSubmissions, setInterestSubmissions] = useState([]);
    const [interestHistory, setInterestHistory] = useState([]);
    const [selectedResponse, setSelectedResponse] = useState(null);

    // Event Reviews State
    const [pendingReviews, setPendingReviews] = useState([]);
    const [approvedReviews, setApprovedReviews] = useState([]);
    const [reviewsLoading, setReviewsLoading] = useState(false);

    const storage = getStorage();

    const fetchEvents = async () => {
        try {
            const querySnapshot = await getDocs(collection(db, "events"));
            const fetched = querySnapshot.docs.map(doc => ({ ...doc.data(), dbId: doc.id }));
            setEvents(fetched);
        } catch (error) {
            console.error("Failed to fetch events:", error);
        }
    };

    const fetchStats = async () => {
        try {
            const statsDoc = await getDoc(doc(db, "metadata", "events_metadata"));
            if (statsDoc.exists()) {
                const data = statsDoc.data();
                setStats(data);
                setStatsHistory(data.history || []);
                if (data.updatedAt) {
                    setStatsLastUpdated(data.updatedAt.toDate());
                }
            }
        } catch (error) {
            console.error("Failed to fetch stats:", error);
        }
    };

    const fetchInterestForm = async () => {
        try {
            const docRef = await getDoc(doc(db, "metadata", "interest_form"));
            if (docRef.exists()) {
                const data = docRef.data();
                setInterestQuestions(data.questions || []);
                setInterestHistory(data.history || []);
                if (data.updatedAt) {
                    setInterestLastUpdated(data.updatedAt.toDate());
                }
            } else {
                setInterestQuestions(["What topics are you most interested in?"]); // Default
            }
        } catch (error) {
            console.error("Failed to fetch interest form:", error);
        }
    };

    const fetchInterestSubmissions = async () => {
        try {
            const querySnapshot = await getDocs(collection(db, "interest_submissions"));
            const fetched = querySnapshot.docs.map(doc => ({ ...doc.data(), dbId: doc.id }));
            fetched.sort((a, b) => {
                const dateA = a.submittedAt ? a.submittedAt.toDate() : new Date(0);
                const dateB = b.submittedAt ? b.submittedAt.toDate() : new Date(0);
                return dateB - dateA;
            });
            setInterestSubmissions(fetched);
        } catch (error) {
            console.error("Failed to fetch interest submissions:", error);
        }
    };

    const fetchPendingReviews = async () => {
        try {
            setReviewsLoading(true);
            const querySnapshot = await getDocs(collection(db, "event_reviews"));
            const allFetched = querySnapshot.docs.map(doc => ({ ...doc.data(), dbId: doc.id }));
            
            const pending = allFetched
                .filter(rev => rev.status === "pending")
                .sort((a, b) => {
                    const dateA = a.createdAt ? a.createdAt.toDate() : new Date(0);
                    const dateB = b.createdAt ? b.createdAt.toDate() : new Date(0);
                    return dateB - dateA;
                });
                
            const approved = allFetched
                .filter(rev => rev.status === "approved")
                .sort((a, b) => {
                    const dateA = a.createdAt ? a.createdAt.toDate() : new Date(0);
                    const dateB = b.createdAt ? b.createdAt.toDate() : new Date(0);
                    return dateB - dateA;
                });
                
            setPendingReviews(pending);
            setApprovedReviews(approved);
        } catch (error) {
            console.error("Failed to fetch pending reviews:", error);
        } finally {
            setReviewsLoading(false);
        }
    };

    const updateReviewStatus = async (reviewId, newStatus) => {
        try {
            await setDoc(doc(db, "event_reviews", reviewId), { status: newStatus }, { merge: true });
            fetchPendingReviews();
        } catch (error) {
            console.error("Failed to update review:", error);
            alert("Failed to update review status");
        }
    };

    useEffect(() => {
        fetchEvents();
        fetchStats();
        fetchInterestForm();
        fetchInterestSubmissions();
        fetchPendingReviews();
    }, []);

    const handleInput = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleFileChange = (e, type) => {
        if (e.target.files.length > 0) {
            if (type === 'image') setImage(e.target.files[0]);
            else if (type === 'qrCode') setQrCode(e.target.files[0]);
            else if (type === 'recapMedia') {
                const newFiles = Array.from(e.target.files);
                setRecapMediaFiles(prev => [...prev, ...newFiles]);
                e.target.value = null; // Reset input so the same file can be selected again if needed
            }
        }
    };

    const removeRecapFile = (indexToRemove) => {
        setRecapMediaFiles(prev => prev.filter((_, idx) => idx !== indexToRemove));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage("");

        try {
            let imageUrl = formData.img || "";
            let qrCodeUrl = formData.qrCodeImg || "";

            if (image) {
                const imgRef = ref(storage, `eventsimage/${Date.now()}_${image.name}`);
                await uploadBytes(imgRef, image);
                imageUrl = await getDownloadURL(imgRef);
            }

            if (qrCode) {
                const qrRef = ref(storage, `events/${Date.now()}_qr_${qrCode.name}`);
                await uploadBytes(qrRef, qrCode);
                qrCodeUrl = await getDownloadURL(qrRef);
            }

            let newRecapMediaUrls = [...(formData.recapMediaUrls || [])];
            if (recapMediaFiles.length > 0) {
                setMessage(`Uploading ${recapMediaFiles.length} media files... Please wait.`);
                const uploadPromises = recapMediaFiles.map(async (file, index) => {
                    const mediaRef = ref(storage, `events/${Date.now()}_recap_${index}_${file.name}`);
                    await uploadBytes(mediaRef, file);
                    return await getDownloadURL(mediaRef);
                });
                
                const urls = await Promise.all(uploadPromises);
                newRecapMediaUrls = newRecapMediaUrls.concat(urls);
                setMessage(`Upload complete. Saving event...`);
            }

            const numericId = editingId ? formData.id : Date.now();

            const attendeesArray = formData.attendeesText 
                ? formData.attendeesText.split(",").map(e => e.trim()).filter(e => e)
                : [];

            const eventData = {
                id: numericId,
                title: formData.title,
                date: formData.date,
                startTime: formData.startTime,
                endTime: formData.endTime,
                location: formData.location,
                eventCost: formData.eventCost,
                topics: formData.topics,
                eventType: formData.eventType,
                img: imageUrl,
                qrCodeImg: qrCodeUrl,
                paymentLink: formData.paymentLink,
                recapText: formData.recapText || "",
                recapImg: formData.recapImg || "",
                recapMediaUrls: newRecapMediaUrls,
                attendees: attendeesArray,
                updatedAt: serverTimestamp()
            };

            if (!editingId) {
                eventData.createdAt = serverTimestamp();
                await addDoc(collection(db, "events"), eventData);
                setMessage("Event successfully added!");
            } else {
                await setDoc(doc(db, "events", editingId), eventData, { merge: true });
                setMessage("Event successfully updated!");
            }
            
            setTimeout(() => setMessage(""), 3000);
            cancelEdit();
            fetchEvents(); 
        } catch (error) {
            console.error("Error saving event:", error);
            setMessage("Failed to save event: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (ev) => {
        setEditingId(ev.dbId);
        setFormData({
            id: ev.id,
            title: ev.title || "",
            date: ev.date || "",
            startTime: ev.startTime || "",
            endTime: ev.endTime || "",
            location: ev.location || "",
            eventCost: ev.eventCost || "",
            topics: ev.topics || "Campus",
            eventType: ev.eventType || "Offline",
            img: ev.img || "",
            qrCodeImg: ev.qrCodeImg || "",
            paymentLink: ev.paymentLink || "",
            recapText: ev.recapText || "",
            recapImg: ev.recapImg || "",
            recapMediaUrls: ev.recapMediaUrls || [],
            attendeesText: ev.attendees ? ev.attendees.join(", ") : ""
        });
        setImage(null);
        setQrCode(null);
        setRecapMediaFiles([]);
        setActiveTab("events");
        setMessage("Editing event: " + ev.title);
    };

    const handleDelete = async (ev) => {
        if (!window.confirm("Are you sure you want to delete '" + ev.title + "'?")) return;
        try {
            await deleteDoc(doc(db, "events", ev.dbId));
            setMessage("Event deleted.");
            setTimeout(() => setMessage(""), 3000);
            fetchEvents();
        } catch (error) {
            console.error("Error deleting:", error);
            setMessage("Failed to delete.");
        }
    };

    const cancelEdit = () => {
        setEditingId(null);
        setFormData({ title: "", date: "", startTime: "", endTime: "", location: "", eventCost: "", topics: "Campus", eventType: "Offline", img: "", qrCodeImg: "", paymentLink: "", recapText: "", recapImg: "", recapMediaUrls: [], attendeesText: "" });
        setImage(null);
        setQrCode(null);
        setRecapMediaFiles([]);
        setMessage("");
    };

    const handleStatsInput = (e) => {
        setStats({ ...stats, [e.target.name]: e.target.value });
    };

    const handleStatsSubmit = async (e) => {
        e.preventDefault();
        setStatsLoading(true);
        setStatsMessage("");
        try {
            const docRef = doc(db, "metadata", "events_metadata");
            const currentDoc = await getDoc(docRef);
            let currentHistory = [];
            let lastUpdate = null;
            let lastStats = null;

            if (currentDoc.exists()) {
                const data = currentDoc.data();
                currentHistory = data.history || [];
                lastUpdate = data.updatedAt;
                lastStats = {
                    totalEvents: data.totalEvents,
                    totalAttendees: data.totalAttendees,
                    totalCities: data.totalCities
                };
            }
            
            if (lastUpdate && lastStats) {
                currentHistory.unshift({
                    ...lastStats,
                    updatedAt: lastUpdate
                });
            }

            await setDoc(docRef, {
                totalEvents: Number(stats.totalEvents) || 0,
                totalAttendees: Number(stats.totalAttendees) || 0,
                totalCities: Number(stats.totalCities) || 0,
                updatedAt: serverTimestamp(),
                history: currentHistory
            });
            
            setStatsHistory(currentHistory);
            setStatsLastUpdated(new Date());
            setStatsMessage("Stats updated successfully!");
            setTimeout(() => setStatsMessage(""), 3000);
        } catch (error) {
            console.error("Error saving stats:", error);
            setStatsMessage("Failed to save stats.");
        } finally {
            setStatsLoading(false);
        }
    };

    const handleInterestQuestionChange = (index, value) => {
        const newQs = [...interestQuestions];
        newQs[index] = value;
        setInterestQuestions(newQs);
    };
    
    const addInterestQuestion = () => {
        setInterestQuestions([...interestQuestions, ""]);
    };
    
    const removeInterestQuestion = (index) => {
        const newQs = interestQuestions.filter((_, i) => i !== index);
        setInterestQuestions(newQs);
    };

    const handleInterestSubmit = async (e) => {
        e.preventDefault();
        setInterestLoading(true);
        setInterestMessage("");
        try {
            const validQs = interestQuestions.filter(q => q.trim() !== "");
            const docRef = doc(db, "metadata", "interest_form");
            const currentDoc = await getDoc(docRef);
            let currentHistory = [];
            let lastUpdate = null;
            let lastQs = [];
            
            if (currentDoc.exists()) {
                const data = currentDoc.data();
                currentHistory = data.history || [];
                lastUpdate = data.updatedAt;
                lastQs = data.questions || [];
            }
            
            if (lastUpdate && lastQs.length > 0) {
                currentHistory.unshift({
                    questions: lastQs,
                    updatedAt: lastUpdate
                });
            }
            
            await setDoc(docRef, {
                questions: validQs,
                updatedAt: serverTimestamp(),
                history: currentHistory
            });
            
            setInterestQuestions(validQs);
            setInterestHistory(currentHistory);
            setInterestMessage("Interest form updated successfully!");
            setTimeout(() => setInterestMessage(""), 3000);
            setInterestLastUpdated(new Date());
        } catch (error) {
            console.error("Error saving interest form:", error);
            setInterestMessage("Failed to save interest form.");
        } finally {
            setInterestLoading(false);
        }
    };

    const styles = {
        container: { padding: 'min(40px, 5vw)', maxWidth: '1200px', margin: '0 auto', fontFamily: "'Syne', sans-serif" },
        header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap' },
        title: { fontSize: 'clamp(1.8rem, 4vw, 2.5rem)', fontWeight: '400', color: '#003049', margin: 0 },
        tabBar: { display: 'flex', gap: '10px', marginBottom: '30px', borderBottom: '1px solid #ddd', flexWrap: 'wrap' },
        tabBtn: { padding: '10px 15px', border: 'none', background: 'transparent', fontSize: 'clamp(14px, 2vw, 16px)', fontWeight: '600', color: '#666', cursor: 'pointer', borderBottom: '2px solid transparent' },
        activeTabBtn: { padding: '10px 15px', border: 'none', background: 'transparent', fontSize: 'clamp(14px, 2vw, 16px)', fontWeight: '600', color: '#003049', cursor: 'pointer', borderBottom: '2px solid #F07167' },
        mainFlex: { display: 'flex', gap: '30px', alignItems: 'flex-start', flexWrap: 'wrap' },
        formSection: { flex: 1, minWidth: 'min(100%, 320px)', background: 'white', padding: 'min(30px, 5vw)', borderRadius: '15px', boxShadow: '0 10px 30px rgba(0,0,0,0.05)', boxSizing: 'border-box' },
        listSection: { flex: 1, minWidth: 'min(100%, 320px)', padding: '10px', height: 'auto', maxHeight: '800px', overflowY: 'auto', boxSizing: 'border-box' },
        inputGroup: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '15px' },
        label: { fontSize: '14px', fontWeight: '600', color: '#003049' },
        input: { padding: '12px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', fontFamily: "'Syne', sans-serif", outline: 'none' },
        submitBtn: { padding: '15px', background: 'linear-gradient(#fffcf8, #fffcf8) padding-box, linear-gradient(90deg, #ff7b00, #ffc107) border-box', color: '#222', borderRadius: '50px', cursor: 'pointer', border: '2px solid transparent', fontWeight: '600', fontSize: 'clamp(14px, 2vw, 18px)', width: '100%', marginTop: '10px', boxShadow: '0 6px 15px rgba(255, 123, 0, 0.15)' },
        msg: { padding: '15px', background: '#eefbf3', color: '#155724', borderRadius: '8px', marginBottom: '20px', border: '1px solid #c8e6c9', fontSize: '14px' },
        errorMsg: { padding: '15px', background: '#faeced', color: '#721c24', borderRadius: '8px', marginBottom: '20px', border: '1px solid #f5c6cb', fontSize: '14px' },
        listTitle: { fontSize: 'clamp(1.3rem, 3vw, 1.5rem)', fontWeight: '400', color: '#003049', marginBottom: '20px' },
        eventCard: { padding: '20px', border: 'none', borderRadius: '15px', marginBottom: '15px', background: 'white', display: 'flex', gap: '15px', alignItems: 'flex-start', boxShadow: '0 4px 20px rgba(0,0,0,0.04)', position: 'relative', flexWrap: 'wrap' },
        eventImg: { width: '80px', height: '60px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #eee' },
        eventInfo: { flex: 1, minWidth: 'min(100%, 200px)' },
        eventName: { margin: '0 0 5px 0', fontSize: '16px', fontWeight: '600', color: '#003049', paddingRight: '40px' },
        eventDetails: { margin: '0', fontSize: '13px', color: '#666', lineHeight: '1.6' },
        cardActions: { position: 'absolute', top: '15px', right: '15px', display: 'flex', flexDirection: 'row', gap: '5px' },
        actionBtn: { padding: '6px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#003049' },
        deleteBtn: { padding: '6px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#F07167' }
    };

    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    const isPastEvent = formData.date && formData.date < todayStr;

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>{editingId ? "Edit Event" : "Manage Events"}</h1>
            </div>

            <div style={styles.tabBar}>
                <button 
                    style={activeTab === 'events' ? styles.activeTabBtn : styles.tabBtn}
                    onClick={() => setActiveTab('events')}
                >
                    Manage Events
                </button>
                <button 
                    style={activeTab === 'stats' ? styles.activeTabBtn : styles.tabBtn}
                    onClick={() => setActiveTab('stats')}
                >
                    Event Metadata
                </button>
                <button 
                    style={activeTab === 'interest' ? styles.activeTabBtn : styles.tabBtn}
                    onClick={() => setActiveTab('interest')}
                >
                    Interest Form
                </button>
                <button 
                    style={activeTab === 'responses' ? styles.activeTabBtn : styles.tabBtn}
                    onClick={() => { setActiveTab('responses'); setSelectedResponse(null); }}
                >
                    User Responses
                </button>
                <button 
                    style={activeTab === 'reviews' ? styles.activeTabBtn : styles.tabBtn}
                    onClick={() => setActiveTab('reviews')}
                >
                    Event Reviews
                </button>
            </div>

            <div style={styles.mainFlex}>
                <div style={styles.formSection}>
                    {activeTab === 'stats' && (
                        <>
                            <h2 style={{...styles.listTitle, marginBottom: '15px'}}>Update Event Metadata</h2>
                            {statsMessage && (
                                <div style={statsMessage.includes("Failed") ? styles.errorMsg : styles.msg}>
                                    {statsMessage}
                                </div>
                            )}
                            <form onSubmit={handleStatsSubmit} style={{marginBottom: '40px', paddingBottom: '30px', borderBottom: '1px solid #eee'}}>
                                <div style={{display: 'flex', gap: '15px', flexWrap: 'wrap'}}>
                                    <div style={{...styles.inputGroup, flex: 1, minWidth: '100px'}}>
                                        <label style={styles.label}>Total Events</label>
                                        <input name="totalEvents" type="number" value={stats.totalEvents} onChange={handleStatsInput} style={styles.input} />
                                    </div>
                                    <div style={{...styles.inputGroup, flex: 1, minWidth: '100px'}}>
                                        <label style={styles.label}>Total Attendees</label>
                                        <input name="totalAttendees" type="number" value={stats.totalAttendees} onChange={handleStatsInput} style={styles.input} />
                                    </div>
                                    <div style={{...styles.inputGroup, flex: 1, minWidth: '100px'}}>
                                        <label style={styles.label}>Total Cities</label>
                                        <input name="totalCities" type="number" value={stats.totalCities} onChange={handleStatsInput} style={styles.input} />
                                    </div>
                                </div>
                                <button type="submit" style={{...styles.submitBtn, marginTop: '5px', padding: '10px'}} disabled={statsLoading}>
                                    {statsLoading ? "Saving Metadata..." : "Save Metadata"}
                                </button>
                            </form>
                        </>
                    )}

                    {activeTab === 'interest' && (
                        <>
                            <h2 style={{...styles.listTitle, marginBottom: '15px'}}>Manage Interest Form</h2>
                            {interestMessage && (
                                <div style={interestMessage.includes("Failed") ? styles.errorMsg : styles.msg}>
                                    {interestMessage}
                                </div>
                            )}
                            <form onSubmit={handleInterestSubmit} style={{marginBottom: '40px', paddingBottom: '30px', borderBottom: '1px solid #eee'}}>
                                {interestLastUpdated && (
                                    <p style={{fontSize: '13px', color: '#666', marginBottom: '15px'}}>
                                        Last updated: {interestLastUpdated.toLocaleString()}
                                    </p>
                                )}
                                {interestQuestions.map((q, idx) => (
                                    <div key={idx} style={{display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'center'}}>
                                        <input 
                                            value={q} 
                                            onChange={(e) => handleInterestQuestionChange(idx, e.target.value)} 
                                            style={{...styles.input, flex: 1}} 
                                            placeholder="Enter question here"
                                            required
                                        />
                                        <button type="button" onClick={() => removeInterestQuestion(idx)} style={{...styles.deleteBtn, background: '#fff0f0', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '45px', height: '45px', padding: 0}}>
                                            ✕
                                        </button>
                                    </div>
                                ))}
                                <div style={{display: 'flex', gap: '15px', marginTop: '20px', flexWrap: 'wrap'}}>
                                    <button type="button" onClick={addInterestQuestion} style={{...styles.submitBtn, flex: 1, minWidth: '140px', marginTop: 0}}>
                                        + Add Question
                                    </button>
                                    <button type="submit" style={{...styles.submitBtn, flex: 1, minWidth: '140px', marginTop: 0}} disabled={interestLoading}>
                                        {interestLoading ? "Saving..." : "Save Questions"}
                                    </button>
                                </div>
                            </form>
                        </>
                    )}

                    {activeTab === 'reviews' && (
                        <div style={styles.mainFlex}>
                            {/* Pending Reviews Section */}
                            <div style={styles.formSection}>
                                <h2 style={{...styles.listTitle, marginBottom: '15px'}}>Pending Reviews</h2>
                                <p style={{margin: '0 0 20px 0', fontSize: '14px', color: '#666'}}>Approve or reject reviews submitted by event attendees.</p>
                                
                                {reviewsLoading ? (
                                    <p style={{ fontSize: '14px', color: '#777' }}>Loading reviews...</p>
                                ) : pendingReviews.length === 0 ? (
                                    <div style={{ padding: '30px', textAlign: 'center', background: '#fdfdfd', border: '1px dashed #ddd', borderRadius: '12px' }}>
                                        <p style={{ margin: 0, fontSize: '15px', color: '#777' }}>No pending reviews to approve.</p>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                        {pendingReviews.map(rev => (
                                            <div key={rev.dbId} style={{ background: '#fff', border: '1px solid #eee', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                                    <div>
                                                        <h4 style={{ margin: '0 0 5px 0', fontSize: '16px', color: '#003049' }}>{rev.eventName}</h4>
                                                        <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>By {rev.userName} ({rev.userEmail})</p>
                                                    </div>
                                                    <div style={{ fontSize: '20px', fontWeight: 'bold' }}>
                                                        {rev.rating >= 5 ? '🤩' : rev.rating >= 4 ? '😊' : rev.rating >= 3 ? '😐' : rev.rating >= 2 ? '😕' : '😞'}
                                                    </div>
                                                </div>
                                                <p style={{ margin: '15px 0', fontSize: '14px', color: '#444', lineHeight: '1.6', background: '#fcfcfc', padding: '15px', borderRadius: '8px', borderLeft: '3px solid #f29053' }}>
                                                    "{rev.reviewText}"
                                                </p>
                                                <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                                                    <button onClick={() => updateReviewStatus(rev.dbId, "approved")} style={{ ...styles.submitBtn, flex: 1, padding: '10px', marginTop: 0, background: '#eefbf3', border: '1px solid #c8e6c9', color: '#155724', boxShadow: 'none' }}>
                                                        Approve
                                                    </button>
                                                    <button onClick={() => updateReviewStatus(rev.dbId, "rejected")} style={{ ...styles.submitBtn, flex: 1, padding: '10px', marginTop: 0, background: '#faeced', border: '1px solid #f5c6cb', color: '#721c24', boxShadow: 'none' }}>
                                                        Reject
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Accepted Reviews Section */}
                            <div style={styles.listSection}>
                                <h2 style={{...styles.listTitle, marginBottom: '15px'}}>Accepted Reviews</h2>
                                <p style={{margin: '0 0 20px 0', fontSize: '14px', color: '#666'}}>These reviews are approved.</p>
                                
                                {reviewsLoading ? (
                                    <p style={{ fontSize: '14px', color: '#777' }}>Loading reviews...</p>
                                ) : approvedReviews.length === 0 ? (
                                    <div style={{ padding: '30px', textAlign: 'center', background: '#fdfdfd', border: '1px dashed #ddd', borderRadius: '12px' }}>
                                        <p style={{ margin: 0, fontSize: '15px', color: '#777' }}>No accepted reviews yet.</p>
                                    </div>
                                ) : (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                                        {approvedReviews.map(rev => (
                                            <div key={rev.dbId} style={{ background: '#fff', border: '1px solid #eee', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                                                    <div>
                                                        <h4 style={{ margin: '0 0 5px 0', fontSize: '16px', color: '#003049' }}>{rev.eventName}</h4>
                                                        <p style={{ margin: 0, fontSize: '13px', color: '#666' }}>By {rev.userName} ({rev.userEmail})</p>
                                                    </div>
                                                    <div style={{ fontSize: '20px', fontWeight: 'bold' }}>
                                                        {rev.rating >= 5 ? '🤩' : rev.rating >= 4 ? '😊' : rev.rating >= 3 ? '😐' : rev.rating >= 2 ? '😕' : '😞'}
                                                    </div>
                                                </div>
                                                <p style={{ margin: '15px 0', fontSize: '14px', color: '#444', lineHeight: '1.6', background: '#fcfcfc', padding: '15px', borderRadius: '8px', borderLeft: '3px solid #155724' }}>
                                                    "{rev.reviewText}"
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'events' && (
                        <>
                            <h2 style={{...styles.listTitle, marginBottom: '15px'}}>{editingId ? "Edit Event Details" : "Add New Event"}</h2>
                            {message && (
                                <div style={message.includes("Failed") ? styles.errorMsg : styles.msg}>
                                    {message}
                                </div>
                            )}

                            <form onSubmit={handleSubmit}>
                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>Event Name</label>
                                    <input name="title" value={formData.title} onChange={handleInput} style={styles.input} required placeholder="Event Name" />
                                </div>
                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>Date</label>
                                    <input name="date" type="date" value={formData.date} onChange={handleInput} style={styles.input} required />
                                </div>
                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>Start Time</label>
                                    <input name="startTime" type="time" value={formData.startTime} onChange={handleInput} style={styles.input} required />
                                </div>
                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>End Time</label>
                                    <input name="endTime" type="time" value={formData.endTime} onChange={handleInput} style={styles.input} required />
                                </div>
                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>Location</label>
                                    <input name="location" value={formData.location} onChange={handleInput} style={styles.input} required placeholder="Location" />
                                </div>
                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>Event Image</label>
                                    {editingId && formData.img && (
                                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                            <img src={formData.img} alt="Current Event" style={{ height: '50px', borderRadius: '4px', border: '1px solid #ddd' }} />
                                            <span style={{ fontSize: '12px', color: '#666', marginLeft: '10px' }}>Current image. Upload new to replace.</span>
                                        </div>
                                    )}
                                    <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, 'image')} style={styles.input} required={!editingId} />
                                </div>

                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>Attendees Emails (comma-separated)</label>
                                    <textarea 
                                        name="attendeesText" 
                                        value={formData.attendeesText} 
                                        onChange={handleInput} 
                                        style={{...styles.input, minHeight: '80px', resize: 'vertical'}} 
                                        placeholder="e.g. user1@example.com, user2@example.com"
                                    />
                                    <p style={{margin: '0', fontSize: '12px', color: '#888'}}>These users will be able to leave a review for this event.</p>
                                </div>

                                {!isPastEvent && (
                                    <>
                                        <div style={styles.inputGroup}>
                                            <label style={styles.label}>QR Code for Payment</label>
                                            {editingId && formData.qrCodeImg && (
                                                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                                                    <img src={formData.qrCodeImg} alt="Current QR" style={{ height: '50px', borderRadius: '4px', border: '1px solid #ddd' }} />
                                                    <span style={{ fontSize: '12px', color: '#666', marginLeft: '10px' }}>Current QR code. Upload new to replace.</span>
                                                </div>
                                            )}
                                            <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, 'qrCode')} style={styles.input} required={!editingId && !formData.qrCodeImg} />
                                        </div>
                                        <div style={styles.inputGroup}>
                                            <label style={styles.label}>Payment / Registration Link (URL)</label>
                                            <input name="paymentLink" value={formData.paymentLink} onChange={handleInput} style={styles.input} placeholder="https://forms.gle/... or payment link" />
                                        </div>
                                        <div style={styles.inputGroup}>
                                            <label style={styles.label}>Event Cost</label>
                                            <input name="eventCost" value={formData.eventCost} onChange={handleInput} style={styles.input} required placeholder="e.g. $40 or ₹40" />
                                        </div>
                                    </>
                                )}
                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>Event Type</label>
                                    <select name="eventType" value={formData.eventType} onChange={handleInput} style={styles.input} required>
                                        <option value="Offline">Offline</option>
                                        <option value="Online">Online</option>
                                    </select>
                                </div>
                                <div style={styles.inputGroup}>
                                    <label style={styles.label}>Category</label>
                                    <select name="topics" value={formData.topics} onChange={handleInput} style={styles.input} required>
                                        <option value="Campus">Campus</option>
                                        <option value="Conversations">Conversations</option>
                                        <option value="Workshops">Workshops</option>
                                        <option value="Conference">Conference</option>
                                        <option value="Summit">Summit</option>
                                        <option value="Unconference">Unconference</option>
                                        <option value="Connections">Connections</option>
                                    </select>
                                </div>

                                {isPastEvent && (
                                    <>
                                        <div style={{...styles.inputGroup, marginTop: '20px', padding: '15px', background: '#f5f5f5', borderRadius: '8px'}}>
                                            <h4 style={{margin: '0 0 10px 0', color: '#003049'}}>Event Recap (For Past Events)</h4>
                                            <label style={styles.label}>Recap Text</label>
                                            <textarea name="recapText" value={formData.recapText} onChange={handleInput} style={{...styles.input, minHeight: '80px', resize: 'vertical', marginBottom: '10px'}} placeholder="Describe how the event went..." />
                                            
                                            <label style={styles.label}>Recap Media (Images & Videos)</label>
                                            {editingId && (formData.recapMediaUrls?.length > 0) && (
                                                <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px', background: '#fff', border: '1px solid #eee', padding: '15px', borderRadius: '10px', marginBottom: '15px' }}>
                                                    <strong style={{ fontSize: '14px', color: '#003049', borderBottom: '1px solid #eee', paddingBottom: '8px', marginBottom: '4px' }}>
                                                        Existing Media ({formData.recapMediaUrls.length} files)
                                                    </strong>
                                                    {formData.recapMediaUrls.map((url, idx) => {
                                                        const isVid = url.split('?')[0].match(/\.(mp4|webm|ogg|mp3)$/i);
                                                        let fileName = `Media File ${idx + 1}`;
                                                        try {
                                                            const decoded = decodeURIComponent(url.split('?')[0]);
                                                            const parts = decoded.split('/');
                                                            fileName = parts[parts.length - 1].split('_').slice(2).join('_') || fileName;
                                                        } catch (e) {}

                                                        return (
                                                        <div key={idx} style={{ 
                                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                            fontSize: '13px', color: '#444', background: '#f9f9f9', 
                                                            padding: '8px 12px', borderRadius: '6px', border: '1px solid #eee',
                                                            transition: 'background 0.2s'
                                                        }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                <div style={{ width: '30px', height: '30px', borderRadius: '4px', overflow: 'hidden', border: '1px solid #ddd' }}>
                                                                    {isVid ? <video src={url} style={{width:'100%', height:'100%', objectFit:'cover'}} /> : <img src={url} style={{width:'100%', height:'100%', objectFit:'cover'}} alt="thumb" />}
                                                                </div>
                                                                <span style={{ fontWeight: '500', wordBreak: 'break-all' }}>{fileName}</span>
                                                            </div>
                                                            <button type="button" onClick={() => {
                                                                const newUrls = [...formData.recapMediaUrls];
                                                                newUrls.splice(idx, 1);
                                                                setFormData({...formData, recapMediaUrls: newUrls});
                                                            }} style={{
                                                                background: 'transparent', border: 'none', color: '#999', cursor: 'pointer',
                                                                padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                borderRadius: '50%', transition: 'background 0.2s, color 0.2s'
                                                            }} onMouseOver={(e) => { e.currentTarget.style.color = '#e63946'; e.currentTarget.style.background = '#ffe5e5'; }} onMouseOut={(e) => { e.currentTarget.style.color = '#999'; e.currentTarget.style.background = 'transparent'; }}>
                                                                ✕
                                                            </button>
                                                        </div>
                                                    )})}
                                                </div>
                                            )}
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                                                <label htmlFor="recap-file-upload" style={{
                                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                                                    padding: '10px 20px', background: '#F07167', color: 'white', borderRadius: '8px',
                                                    cursor: 'pointer', fontWeight: '600', fontSize: '14px', transition: 'background 0.3s'
                                                }}>
                                                    <span style={{ fontSize: '18px' }}>+</span> Add Media
                                                </label>
                                                <input id="recap-file-upload" type="file" multiple accept="image/*,video/mp4,audio/mp3,video/webm" onChange={(e) => handleFileChange(e, 'recapMedia')} style={{ display: 'none' }} />
                                            </div>
                                            
                                            {recapMediaFiles.length > 0 && (
                                                <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px', background: '#fff', border: '1px solid #eee', padding: '15px', borderRadius: '10px' }}>
                                                    <strong style={{ fontSize: '14px', color: '#003049', borderBottom: '1px solid #eee', paddingBottom: '8px', marginBottom: '4px' }}>Upload Queue ({recapMediaFiles.length} files)</strong>
                                                    {recapMediaFiles.map((file, idx) => (
                                                        <div key={idx} style={{ 
                                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                            fontSize: '13px', color: '#444', background: '#f9f9f9', 
                                                            padding: '8px 12px', borderRadius: '6px', border: '1px solid #eee',
                                                            transition: 'background 0.2s'
                                                        }}>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                <span style={{ color: '#F07167', fontSize: '16px' }}>{file.type.startsWith('video') ? '🎬' : '📸'}</span>
                                                                <span style={{ fontWeight: '500' }}>{file.name}</span>
                                                            </div>
                                                            <button type="button" onClick={() => removeRecapFile(idx)} style={{
                                                                background: 'transparent', border: 'none', color: '#999', cursor: 'pointer',
                                                                padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                borderRadius: '50%', transition: 'background 0.2s, color 0.2s'
                                                            }} onMouseOver={(e) => { e.currentTarget.style.color = '#e63946'; e.currentTarget.style.background = '#ffe5e5'; }} onMouseOut={(e) => { e.currentTarget.style.color = '#999'; e.currentTarget.style.background = 'transparent'; }}>
                                                                ✕
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    </>
                                )}

                                <div style={{display: 'flex', gap: '10px', flexWrap: 'wrap'}}>
                                    <button type="submit" style={{...styles.submitBtn, flex: 1, minWidth: '140px'}} disabled={loading}>
                                        {loading ? (message ? message : "Saving...") : (editingId ? "Update Event" : "Add Event")}
                                    </button>
                                    {editingId && (
                                        <button type="button" onClick={cancelEdit} style={{...styles.submitBtn, flex: 1, minWidth: '140px', background: '#eee', color: '#333', border: 'none', boxShadow: 'none'}}>
                                            Cancel
                                        </button>
                                    )}
                                </div>
                            </form>
                        </>
                    )}

                    {activeTab === 'responses' && (
                        <>
                            <h2 style={{...styles.listTitle, marginBottom: '15px'}}>User Responses ({interestSubmissions.length})</h2>
                            {interestSubmissions.length === 0 ? (
                                <p style={{ fontSize: '14px', color: '#777' }}>No submissions yet.</p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                    {interestSubmissions.map(sub => (
                                        <div key={sub.dbId} style={{
                                            ...styles.eventCard, 
                                            marginBottom: 0, 
                                            border: selectedResponse?.dbId === sub.dbId ? '2px solid #f29053' : '1px solid #eee',
                                            cursor: 'pointer'
                                        }} onClick={() => { setSelectedResponse(sub); setExpandedQuestionIndex(0); setIsResponseMinimized(false); }}>
                                            <div style={{ flex: 1 }}>
                                                <p style={{margin: '0 0 5px 0', fontSize: '15px', fontWeight: '600', color: '#003049'}}>
                                                    {sub.userEmail || sub.email || (sub.user ? sub.user.email : "Anonymous")}
                                                </p>
                                                <p style={{margin: '0', fontSize: '13px', color: '#666'}}>
                                                    {sub.submittedAt ? sub.submittedAt.toDate().toLocaleString() : "Unknown date"}
                                                </p>
                                            </div>
                                            <button style={{...styles.submitBtn, width: 'auto', padding: '8px 15px', marginTop: 0, fontSize: '14px', background: selectedResponse?.dbId === sub.dbId ? '#f29053' : '#F07167'}} onClick={(e) => { e.stopPropagation(); setSelectedResponse(sub); setExpandedQuestionIndex(0); setIsResponseMinimized(false); }}>
                                                View
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>

                <div className="no-scrollbar" style={styles.listSection}>
                    {activeTab === 'events' && (
                        <>
                            <h2 style={styles.listTitle}>Live Events ({events.length})</h2>
                            {events.length === 0 ? (
                                <p style={{ fontSize: '14px', color: '#777' }}>No upcoming events found.</p>
                            ) : (
                                events.map(ev => (
                                    <div key={ev.dbId} style={styles.eventCard}>
                                        <img src={ev.img || "/placeholder.jpg"} alt={ev.title} style={styles.eventImg} />
                                        <div style={styles.eventInfo}>
                                            <h3 style={styles.eventName}>{ev.title}</h3>
                                            <p style={styles.eventDetails}>
                                                <strong>Date:</strong> {ev.date} from {ev.startTime} to {ev.endTime} <br />
                                                <strong>Location:</strong> {ev.location} <br />
                                                <strong>Cost:</strong> {ev.eventCost}
                                            </p>
                                        </div>
                                        <div style={styles.cardActions}>
                                            <button onClick={() => handleEdit(ev)} style={styles.actionBtn} title="Edit Event">
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M12 20h9"></path>
                                                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path>
                                                </svg>
                                            </button>
                                            <button onClick={() => handleDelete(ev)} style={styles.deleteBtn} title="Delete Event">
                                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="3 6 5 6 21 6"></polyline>
                                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                ))
                            )}
                        </>
                    )}

                    {activeTab === 'interest' && (
                        <>
                            {/* Live Interest Form Card */}
                            <div style={{
                                background: '#ffe5d3', 
                                border: '1px solid #fae7d8', 
                                borderRadius: '16px', 
                                padding: '30px',
                                boxShadow: '0 4px 15px rgba(0,0,0,0.02)'
                            }}>
                                <h2 style={{ margin: '0 0 5px 0', fontSize: '24px', color: '#003049', fontWeight: '700' }}>Live Interest Form</h2>
                                <p style={{ margin: '0 0 20px 0', fontSize: '14px', color: '#888' }}>
                                    Last Updated: {interestLastUpdated ? interestLastUpdated.toLocaleString() : "Unknown"}
                                </p>
                                
                                <div style={{
                                    background: '#ffffff',
                                    border: '1px solid #f6f0ea',
                                    borderRadius: '12px',
                                    padding: '25px'
                                }}>
                                    {interestQuestions.length === 0 ? (
                                        <p style={{ fontSize: '14px', color: '#777' }}>No questions configured.</p>
                                    ) : (
                                        <ul style={{ margin: 0, padding: 0, listStyle: 'none', color: '#8b5c57', fontSize: '15px', lineHeight: '1.7' }}>
                                            {interestQuestions.map((q, idx) => (
                                                <li key={idx} style={{ marginBottom: '15px', display: 'flex', alignItems: 'flex-start' }}>
                                                    <span style={{ color: '#f29053', marginRight: '12px', fontSize: '18px', lineHeight: '1.2' }}>•</span>
                                                    <span>{q}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            </div>
                            
                            {interestHistory.length > 0 && (
                                <div style={{marginTop: '30px'}}>
                                    <h2 style={{...styles.listTitle, marginBottom: '15px'}}>Previous Versions</h2>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                        {interestHistory.map((hist, index) => (
                                            <div key={index} style={{...styles.eventCard, flexDirection: 'column', alignItems: 'center', marginBottom: 0, background: '#fff', border: '1px solid #fae7d8', padding: '30px 20px'}}>
                                                
                                                {/* Mindfulness/Wellness Card UI */}
                                                <div style={{
                                                    background: 'linear-gradient(135deg, #fff9f5 0%, #ffe6d6 100%)', 
                                                    borderRadius: '16px',
                                                    padding: '30px',
                                                    width: '95%',
                                                    color: '#5c463c',
                                                    boxShadow: '0 8px 25px rgba(242, 144, 83, 0.1)',
                                                    position: 'relative',
                                                    border: '1px solid #fde0cc'
                                                }}>
                                                    {/* Decorative subtle element */}
                                                    <div style={{ position: 'absolute', top: '15px', right: '20px', opacity: 0.15, fontSize: '50px', color: '#f29053', fontFamily: 'serif', lineHeight: 1 }}>”</div>
                                                    
                                                    <ul style={{ margin: 0, padding: 0, listStyle: 'none', fontSize: '14px', lineHeight: '1.8' }}>
                                                        {hist.questions && hist.questions.map((q, idx) => (
                                                            <li key={idx} style={{ marginBottom: '15px', display: 'flex', alignItems: 'flex-start' }}>
                                                                <span style={{ color: '#f29053', marginRight: '12px', fontSize: '16px', lineHeight: '1.4' }}>✧</span>
                                                                <span style={{ fontWeight: '500' }}>{q}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </div>
                                                
                                                <div style={{width: '100%', borderTop: '1px dashed #fde0cc', paddingTop: '20px', marginTop: '20px', textAlign: 'center'}}>
                                                    <p style={{margin: '0', fontSize: '12px', color: '#999', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 'bold'}}>
                                                        Saved At: {hist.updatedAt && hist.updatedAt.toDate ? hist.updatedAt.toDate().toLocaleString() : (hist.updatedAt instanceof Date ? hist.updatedAt.toLocaleString() : "Unknown")}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {activeTab === 'responses' && (
                        <>
                            <h2 style={styles.listTitle}>Response Details</h2>
                            {selectedResponse ? (
                                <div style={{...styles.eventCard, flexDirection: 'column', alignItems: 'flex-start', gap: '15px'}}>
                                    <div 
                                        style={{width: '100%', borderBottom: isResponseMinimized ? 'none' : '1px solid #eee', paddingBottom: isResponseMinimized ? '0' : '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer'}}
                                        onClick={() => setIsResponseMinimized(!isResponseMinimized)}
                                    >
                                        <div>
                                            <p style={{margin: '0 0 5px 0', fontSize: '16px', fontWeight: '600', color: '#003049'}}>
                                                {selectedResponse.userEmail || selectedResponse.email || (selectedResponse.user ? selectedResponse.user.email : "Anonymous")}
                                            </p>
                                            <p style={{margin: '0', fontSize: '14px', color: '#666'}}>
                                                <strong>Submitted At:</strong> {selectedResponse.submittedAt ? selectedResponse.submittedAt.toDate().toLocaleString() : "Unknown"}
                                            </p>
                                        </div>
                                        <span style={{fontSize: '24px', color: '#666', fontWeight: '300'}}>
                                            {isResponseMinimized ? '+' : '−'}
                                        </span>
                                    </div>
                                    {!isResponseMinimized && (
                                        <div style={{width: '100%', animation: 'fadeIn 0.2s ease-out'}}>
                                            {selectedResponse.answers && Object.entries(selectedResponse.answers).map(([question, answer], idx) => (
                                            <div key={idx} style={{marginBottom: '10px', background: '#ffd1bb', borderRadius: '8px', border: '1px solid #eee', overflow: 'hidden'}}>
                                                <div 
                                                    onClick={() => setExpandedQuestionIndex(expandedQuestionIndex === idx ? null : idx)}
                                                    style={{padding: '16px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: expandedQuestionIndex === idx ? '#ffe5d3' : '#ffd1bb', transition: 'background 0.2s'}}
                                                >
                                                    <strong style={{fontSize: '14px', color: '#b87764', paddingRight: '15px', lineHeight: '1.4'}}>{question}</strong>
                                                    <span style={{fontSize: '20px', color: '#b87764', fontWeight: '300'}}>{expandedQuestionIndex === idx ? '−' : '+'}</span>
                                                </div>
                                                {expandedQuestionIndex === idx && (
                                                    <div style={{padding: '16px', borderTop: '1px solid #ffd1bb', background: '#ffe5d3', animation: 'fadeIn 0.2s ease-out'}}>
                                                        <p style={{margin: '0', fontSize: '15px', color: '#8b5c57', lineHeight: '1.6'}}>{answer}</p>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px', color: '#999'}}>
                                    <p>Select a user on the left to view their responses.</p>
                                </div>
                            )}
                        </>
                    )}

                    {activeTab === 'stats' && (
                        <>
                            <h2 style={styles.listTitle}>Live Event Metadata</h2>
                            <div style={{...styles.eventCard, flexDirection: 'column', alignItems: 'flex-start'}}>
                                <div style={{width: '100%', borderBottom: '1px solid #eee', paddingBottom: '15px', marginBottom: '15px'}}>
                                    <h3 style={styles.eventName}>Current Metadata Stats</h3>
                                    <p style={{margin: '0', fontSize: '13px', color: '#666'}}>
                                        <strong>Last Updated:</strong> {statsLastUpdated ? statsLastUpdated.toLocaleString() : "Unknown"}
                                    </p>
                                </div>
                                <div style={{width: '100%'}}>
                                    <p style={{margin: '0 0 10px 0', fontSize: '15px', color: '#333'}}>
                                        <strong>Total Events:</strong> {stats.totalEvents || 0}
                                    </p>
                                    <p style={{margin: '0 0 10px 0', fontSize: '15px', color: '#333'}}>
                                        <strong>Total Attendees:</strong> {stats.totalAttendees || 0}
                                    </p>
                                    <p style={{margin: '0 0 10px 0', fontSize: '15px', color: '#333'}}>
                                        <strong>Total Cities:</strong> {stats.totalCities || 0}
                                    </p>
                                </div>
                            </div>
                            
                            {statsHistory.length > 0 && (
                                <div style={{marginTop: '30px'}}>
                                    <h2 style={{...styles.listTitle, marginBottom: '15px'}}>Previous Versions</h2>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                                        {statsHistory.map((hist, index) => (
                                            <div key={index} style={{...styles.eventCard, flexDirection: 'column', alignItems: 'flex-start', marginBottom: 0}}>
                                                <div style={{width: '100%', borderBottom: '1px solid #eee', paddingBottom: '10px', marginBottom: '10px'}}>
                                                    <p style={{margin: '0', fontSize: '13px', color: '#666'}}>
                                                        <strong>Saved At:</strong> {hist.updatedAt && hist.updatedAt.toDate ? hist.updatedAt.toDate().toLocaleString() : (hist.updatedAt instanceof Date ? hist.updatedAt.toLocaleString() : "Unknown")}
                                                    </p>
                                                </div>
                                                <div style={{width: '100%'}}>
                                                    <p style={{margin: '0 0 5px 0', fontSize: '14px', color: '#333'}}>
                                                        <strong>Total Events:</strong> {hist.totalEvents}
                                                    </p>
                                                    <p style={{margin: '0 0 5px 0', fontSize: '14px', color: '#333'}}>
                                                        <strong>Total Attendees:</strong> {hist.totalAttendees}
                                                    </p>
                                                    <p style={{margin: '0 0 5px 0', fontSize: '14px', color: '#333'}}>
                                                        <strong>Total Cities:</strong> {hist.totalCities}
                                                    </p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
